/**
 * Plan reveal screen — shown after intake is saved and plan generation ends.
 * The generation outcome is explicit so a network failure can never be
 * presented as a deliberate skip.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, ScrollView, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import AppHeaderBar from '../layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { useResponsive } from '../../hooks/useResponsive';
import { DevelopmentPlan } from '../../types';
import SampleReportViewer from './SampleReportViewer';
import {
  ONBOARDING_COLORS as COLORS,
  ONBOARDING_CONTENT_MAX_WIDTH,
  ONBOARDING_CONTROL_HEIGHT,
  ONBOARDING_RADII,
  ONBOARDING_REVEAL_TRANSITION_MS,
  ONBOARDING_SPACING,
  ONBOARDING_TYPOGRAPHY,
} from './onboardingTheme';
import { asChecklistStepKey, previewChecklist } from '../mentorEngagements/setupChecklist';

export type PlanGenerationOutcome = 'generated' | 'skipped' | 'failed';

interface Props {
  plan: DevelopmentPlan | null;
  profileType?: string | null;
  outcome: PlanGenerationOutcome;
  onContinue: () => void;
  onRetry: () => void;
  continuing: boolean;
  /** Names the actual destination when intake runs before payment. */
  continueLabel?: string;
  /**
   * The CTA really opens Mentees (tier3). When false, the mentor workspace
   * card names the actual destination instead of "Open Mentees".
   */
  opensMentees?: boolean;
}

type Translate = ReturnType<typeof useTranslation>['t'];

export default function OnboardingPlanReveal({
  plan,
  profileType,
  outcome,
  onContinue,
  onRetry,
  continuing,
  continueLabel,
  opensMentees = true,
}: Props) {
  const { t, language } = useTranslation();
  const insets = useSafeAreaInsets();
  const { isPhone } = useResponsive();
  const [showSampleReport, setShowSampleReport] = useState(false);
  const continueMutexRef = useRef(false);
  const retryMutexRef = useRef(false);
  const revealProgress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(revealProgress, {
      toValue: 1,
      duration: ONBOARDING_REVEAL_TRANSITION_MS,
      useNativeDriver: true,
    }).start();
  }, [revealProgress]);

  useEffect(() => {
    if (!continuing) continueMutexRef.current = false;
  }, [continuing]);

  const guardedContinue = () => {
    if (continueMutexRef.current || continuing) return;
    continueMutexRef.current = true;
    onContinue();
  };

  const guardedRetry = () => {
    if (retryMutexRef.current) return;
    retryMutexRef.current = true;
    onRetry();
  };

  // A generated outcome without a plan is still a failure, never a skip.
  const displayedOutcome: PlanGenerationOutcome = outcome === 'generated' && !plan
    ? 'failed'
    : outcome;

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: COLORS.surface }}>
      <AppHeaderBar />
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: ONBOARDING_SPACING.xl,
          paddingTop: ONBOARDING_SPACING.xxl,
          paddingBottom: Math.max(
            insets.bottom + ONBOARDING_SPACING.xxl,
            ONBOARDING_SPACING.xl * 2,
          ),
        }}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View
          style={{
            width: '100%',
            maxWidth: ONBOARDING_CONTENT_MAX_WIDTH,
            alignSelf: 'center',
            opacity: revealProgress,
            transform: [{
              translateY: revealProgress.interpolate({
                inputRange: [0, 1],
                outputRange: [ONBOARDING_SPACING.lg, 0],
              }),
            }],
          }}
        >
          {displayedOutcome === 'generated' && plan ? (
            <PlanCard
              mentor={plan.plan_kind === 'mentor_workspace' || (!plan.plan_kind && profileType === 'mentor')}
              mentorPlan={plan.plan_kind === 'mentor_plan'}
              plan={plan}
              continuing={continuing}
              continueLabel={continueLabel}
              opensMentees={opensMentees}
              isPhone={isPhone}
              onContinue={guardedContinue}
              onOpenSample={() => setShowSampleReport(true)}
              t={t}
            />
          ) : displayedOutcome === 'skipped' ? (
            <SkippedCard
              continuing={continuing}
              continueLabel={continueLabel}
              isPhone={isPhone}
              onContinue={guardedContinue}
              onOpenSample={() => setShowSampleReport(true)}
              t={t}
            />
          ) : (
            <FailedCard
              continuing={continuing}
              continueLabel={continueLabel}
              isPhone={isPhone}
              onContinue={guardedContinue}
              onRetry={guardedRetry}
              t={t}
            />
          )}
        </Animated.View>
      </ScrollView>
      <SampleReportViewer
        visible={showSampleReport}
        surface="plan_reveal"
        primaryAction="continue"
        language={language}
        onClose={() => setShowSampleReport(false)}
        onPrimaryPress={() => {
          setShowSampleReport(false);
          guardedContinue();
        }}
      />
    </SafeAreaView>
  );
}

interface SharedCardProps {
  continuing: boolean;
  continueLabel?: string;
  isPhone: boolean;
  onContinue: () => void;
  t: Translate;
}

interface PlanCardProps extends SharedCardProps {
  mentor: boolean;
  /** The mentor's mentoring-craft plan: the coach plan layout, then the setup checklist as its own card. */
  mentorPlan: boolean;
  opensMentees: boolean;
  plan: DevelopmentPlan;
  onOpenSample: () => void;
}

function PlanCard({
  mentor,
  mentorPlan,
  opensMentees,
  plan,
  continuing,
  continueLabel,
  isPhone,
  onContinue,
  onOpenSample,
  t,
}: PlanCardProps) {
  if (mentor) return (
    <Card className="py-0" style={{ borderRadius: ONBOARDING_RADII.lg }}>
      <CardContent style={{ padding: ONBOARDING_SPACING.xl, gap: ONBOARDING_SPACING.lg }}>
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.caption, color: COLORS.primary }}>{t('intake.mentorWorkspace.eyebrow')}</Text>
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.title, color: COLORS.text.primary }}>{plan.headline}</Text>
        {!!plan.summary && <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.secondary }}>{plan.summary}</Text>}
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.subtitle, color: COLORS.text.primary }}>{t('mentorChecklist.revealTitle')}</Text>
        <ChecklistPreview t={t} />
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.secondary }}>{t('mentorChecklist.previewNote')}</Text>
        <ContinueButton
          continuing={continuing}
          label={opensMentees
            ? t('intake.mentorWorkspace.openMentees')
            : continueLabel ?? t('intake.plan.continueCta')}
          onPress={onContinue}
        />
      </CardContent>
    </Card>
  );
  const startStep = mentorPlan ? asChecklistStepKey(plan.start_here_step) : null;
  const planButtons = (
    <>
      <Button
        variant="outline"
        size="default"
        onPress={onOpenSample}
        disabled={continuing}
        accessibilityLabel={t('intake.plan.sampleAnalysisCta')}
        accessibilityState={{ disabled: continuing }}
        style={{ minHeight: ONBOARDING_CONTROL_HEIGHT, borderRadius: ONBOARDING_RADII.md }}
      >
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.button, color: COLORS.primary }}>
          {t('intake.plan.sampleAnalysisCta')}
        </Text>
      </Button>
      <ContinueButton
        continuing={continuing}
        label={mentorPlan && opensMentees
          ? t('intake.mentorWorkspace.openMentees')
          : continueLabel ?? t('intake.plan.continueCta')}
        onPress={onContinue}
      />
    </>
  );
  const planCard = (
    <Card testID={mentorPlan ? 'onboarding_mentor_plan' : undefined} className="py-0" style={{ borderRadius: ONBOARDING_RADII.lg }}>
      <CardContent style={{ padding: ONBOARDING_SPACING.xl, gap: ONBOARDING_SPACING.xl }}>
        <View
          style={{
            flexDirection: isPhone ? 'column' : 'row',
            alignItems: isPhone ? 'flex-start' : 'center',
            gap: ONBOARDING_SPACING.md,
          }}
        >
          <View
            className="items-center justify-center"
            style={{
              width: ONBOARDING_CONTROL_HEIGHT,
              height: ONBOARDING_CONTROL_HEIGHT,
              borderRadius: ONBOARDING_RADII.lg,
              backgroundColor: COLORS.primary,
            }}
          >
            <MaterialIcons name="auto-awesome" size={ONBOARDING_SPACING.xl} color={COLORS.onPrimary} />
          </View>
          <View style={{ flex: isPhone ? undefined : 1, minWidth: 0 }}>
            <Text
              className="uppercase tracking-wide"
              style={{ ...ONBOARDING_TYPOGRAPHY.caption, color: COLORS.primary, fontWeight: '600' }}
            >
              {mentorPlan ? t('mentorPlan.eyebrow') : t('intake.plan.eyebrow')}
            </Text>
            <Text style={{ ...ONBOARDING_TYPOGRAPHY.title, color: COLORS.text.primary }}>
              {plan.headline}
            </Text>
          </View>
        </View>
        {mentorPlan && !!plan.summary && (
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.secondary }}>{plan.summary}</Text>
        )}

        {plan.current_level && plan.target_level && plan.target_level !== 'exploring' ? (
          <View className="flex-row items-center flex-wrap" style={{ gap: ONBOARDING_SPACING.sm }}>
            <LevelChip label={plan.current_level} muted t={t} />
            <MaterialIcons name="arrow-forward" size={ONBOARDING_SPACING.lg} color={COLORS.text.muted} />
            <LevelChip label={plan.target_level} t={t} />
          </View>
        ) : null}

        <Divider />

        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.subtitle, color: COLORS.text.primary }}>
            {t('intake.plan.focusHeading')}
          </Text>
          {plan.focus_areas.map((area, index) => (
            <View
              key={`${area.competency_code || index}-${area.title}`}
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: ONBOARDING_SPACING.md,
                paddingVertical: ONBOARDING_SPACING.md,
                borderBottomWidth: index === plan.focus_areas.length - 1 ? 0 : 1,
                borderBottomColor: COLORS.border,
              }}
            >
              <View
                className="items-center justify-center"
                style={{
                  width: ONBOARDING_SPACING.xxl,
                  height: ONBOARDING_SPACING.xxl,
                  borderRadius: ONBOARDING_RADII.sm,
                  backgroundColor: COLORS.primarySubtle,
                }}
              >
                <Text style={{ ...ONBOARDING_TYPOGRAPHY.caption, color: COLORS.primary, fontWeight: '700' }}>
                  {index + 1}
                </Text>
              </View>
              <View style={{ flex: 1, minWidth: 0, gap: ONBOARDING_SPACING.xs }}>
                <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.primary, fontWeight: '600' }}>
                  {area.title}
                </Text>
                <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.secondary }}>
                  {area.why}
                </Text>
                {area.practice ? (
                  <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.secondary }}>
                    {t('intake.plan.practiceText', { practice: area.practice })}
                  </Text>
                ) : null}
              </View>
            </View>
          ))}
        </View>

        <Divider />

        <PlanDetail label={t('intake.plan.nextMilestoneLabel')} value={plan.next_milestone} />
        <PlanDetail label={t('intake.plan.weeklyPracticeLabel')} value={plan.weekly_practice} />

        <View
          style={{
            padding: ONBOARDING_SPACING.lg,
            borderRadius: ONBOARDING_RADII.sm,
            backgroundColor: COLORS.successSubtle,
            borderWidth: 1,
            borderColor: COLORS.success,
            gap: ONBOARDING_SPACING.xs,
          }}
        >
          <Text
            className="uppercase tracking-wide"
            style={{ ...ONBOARDING_TYPOGRAPHY.caption, color: COLORS.success, fontWeight: '600' }}
          >
            {t('intake.plan.firstActionLabel')}
          </Text>
          {startStep ? (
            <Text testID="onboarding_mentor_plan_start_step" style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.secondary, fontWeight: '600' }}>
              {t('mentorPlan.checklistStep', { title: t(`mentorChecklist.steps.${startStep}.title`) })}
            </Text>
          ) : null}
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.primary, fontWeight: '600' }}>
            {plan.first_action}
          </Text>
        </View>
        {mentorPlan ? null : planButtons}
      </CardContent>
    </Card>
  );
  if (!mentorPlan) return planCard;
  return (
    <View style={{ gap: ONBOARDING_SPACING.lg }}>
      {planCard}
      <Card className="py-0" style={{ borderRadius: ONBOARDING_RADII.lg }}>
        <CardContent style={{ padding: ONBOARDING_SPACING.xl, gap: ONBOARDING_SPACING.lg }}>
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.subtitle, color: COLORS.text.primary }}>{t('mentorChecklist.revealTitle')}</Text>
          <ChecklistPreview t={t} />
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.secondary }}>{t('mentorChecklist.previewNote')}</Text>
          {planButtons}
        </CardContent>
      </Card>
    </View>
  );
}


interface SkippedCardProps extends SharedCardProps {
  onOpenSample: () => void;
}

function SkippedCard({
  continuing,
  continueLabel,
  isPhone,
  onContinue,
  onOpenSample,
  t,
}: SkippedCardProps) {
  return (
    <Card className="py-0" style={{ borderRadius: ONBOARDING_RADII.lg }}>
      <CardContent style={{ padding: ONBOARDING_SPACING.xl, gap: ONBOARDING_SPACING.lg }}>
        <RevealHeader
          icon="bookmark-outline"
          isPhone={isPhone}
          title={t('intake.plan.skippedTitle')}
        />
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.secondary }}>
          {t('intake.plan.skippedBody')}
        </Text>
        <Button
          variant="outline"
          size="default"
          onPress={onOpenSample}
          disabled={continuing}
          accessibilityLabel={t('intake.plan.sampleAnalysisCta')}
          accessibilityState={{ disabled: continuing }}
          style={{ minHeight: ONBOARDING_CONTROL_HEIGHT, borderRadius: ONBOARDING_RADII.md }}
        >
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.button, color: COLORS.primary }}>
            {t('intake.plan.sampleAnalysisCta')}
          </Text>
        </Button>
        <ContinueButton
          continuing={continuing}
          label={continueLabel ?? t('intake.results.continue')}
          onPress={onContinue}
        />
      </CardContent>
    </Card>
  );
}

interface FailedCardProps extends SharedCardProps {
  onRetry: () => void;
}

function FailedCard({
  continuing,
  continueLabel,
  isPhone,
  onContinue,
  onRetry,
  t,
}: FailedCardProps) {
  return (
    <Card
      className="py-0"
      style={{ borderRadius: ONBOARDING_RADII.lg }}
      accessibilityRole="alert"
      accessibilityLiveRegion="assertive"
    >
      <CardContent style={{ padding: ONBOARDING_SPACING.xl, gap: ONBOARDING_SPACING.lg }}>
        <RevealHeader
          icon="error-outline"
          iconColor={COLORS.error}
          iconBackground={COLORS.errorSubtle}
          isPhone={isPhone}
          title={t('intake.plan.generationFailedTitle')}
        />
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.secondary }}>
          {t('intake.plan.generationFailedBody')}
        </Text>
        <Button
          variant="default"
          size="default"
          onPress={onRetry}
          disabled={continuing}
          accessibilityLabel={t('intake.plan.retryCta')}
          accessibilityState={{ disabled: continuing }}
          style={({ pressed }) => ({
            minHeight: ONBOARDING_CONTROL_HEIGHT,
            borderRadius: ONBOARDING_RADII.md,
            backgroundColor: pressed ? COLORS.primaryPressed : COLORS.primary,
          })}
        >
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.button, color: COLORS.onPrimary }}>
            {t('intake.plan.retryCta')}
          </Text>
        </Button>
        <Button
          variant="outline"
          size="default"
          onPress={onContinue}
          disabled={continuing}
          accessibilityLabel={continueLabel ?? t('intake.results.continue')}
          accessibilityState={{ disabled: continuing, busy: continuing }}
          style={{ minHeight: ONBOARDING_CONTROL_HEIGHT, borderRadius: ONBOARDING_RADII.md }}
        >
          {continuing ? (
            <ActivityIndicator size="small" color={COLORS.primary} />
          ) : (
            <Text style={{ ...ONBOARDING_TYPOGRAPHY.button, color: COLORS.primary }}>
              {continueLabel ?? t('intake.results.continue')}
            </Text>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

interface RevealHeaderProps {
  icon: keyof typeof MaterialIcons.glyphMap;
  iconBackground?: string;
  iconColor?: string;
  isPhone: boolean;
  title: string;
}

function RevealHeader({
  icon,
  iconBackground = COLORS.primary,
  iconColor = COLORS.onPrimary,
  isPhone,
  title,
}: RevealHeaderProps) {
  return (
    <View
      style={{
        flexDirection: isPhone ? 'column' : 'row',
        alignItems: isPhone ? 'flex-start' : 'center',
        gap: ONBOARDING_SPACING.md,
      }}
    >
      <View
        className="items-center justify-center"
        style={{
          width: ONBOARDING_CONTROL_HEIGHT,
          height: ONBOARDING_CONTROL_HEIGHT,
          borderRadius: ONBOARDING_RADII.lg,
          backgroundColor: iconBackground,
        }}
      >
        <MaterialIcons name={icon} size={ONBOARDING_SPACING.xl} color={iconColor} />
      </View>
      <Text
        style={{
          ...ONBOARDING_TYPOGRAPHY.title,
          color: COLORS.text.primary,
          flex: isPhone ? undefined : 1,
          minWidth: 0,
        }}
      >
        {title}
      </Text>
    </View>
  );
}

function ContinueButton({
  continuing,
  label,
  onPress,
}: {
  continuing: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <Button
      variant="default"
      size="default"
      onPress={onPress}
      disabled={continuing}
      accessibilityLabel={label}
      accessibilityState={{ disabled: continuing, busy: continuing }}
      style={({ pressed }) => ({
        minHeight: ONBOARDING_CONTROL_HEIGHT,
        borderRadius: ONBOARDING_RADII.md,
        backgroundColor: continuing
          ? COLORS.primaryDisabled
          : pressed
            ? COLORS.primaryPressed
            : COLORS.primary,
      })}
    >
      {continuing ? (
        <ActivityIndicator size="small" color={COLORS.onPrimary} />
      ) : (
        <Text style={{ ...ONBOARDING_TYPOGRAPHY.button, color: COLORS.onPrimary }}>
          {label}
        </Text>
      )}
    </Button>
  );
}

/** F1: the same steps as the live checklist, each with its own success marker. */
function ChecklistPreview({ t }: { t: Translate }) {
  return (
    <View testID="onboarding_mentor_checklist" style={{ gap: ONBOARDING_SPACING.md }}>
      {previewChecklist(null, false).steps.map((step, index) => (
        <View key={step.key} testID={`onboarding_mentor_checklist_${step.key}`} style={{ gap: ONBOARDING_SPACING.xs }}>
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.primary }}>
            {index + 1}. {t(`mentorChecklist.steps.${step.key}.title`)}
          </Text>
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.text.secondary }}>
            {t(`mentorChecklist.steps.${step.key}.why`)}
          </Text>
          <Text style={{ ...ONBOARDING_TYPOGRAPHY.caption, color: COLORS.text.muted }}>
            {t('mentorChecklist.successLabel')}: {t(`mentorChecklist.steps.${step.key}.done`)}
          </Text>
        </View>
      ))}
    </View>
  );
}

function Divider() {
  return <View style={{ height: 1, backgroundColor: COLORS.border }} />;
}

function PlanDetail({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ gap: ONBOARDING_SPACING.xs }}>
      <Text
        className="uppercase tracking-wide"
        style={{ ...ONBOARDING_TYPOGRAPHY.caption, color: COLORS.text.muted, fontWeight: '600' }}
      >
        {label}
      </Text>
      <Text style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.primary }}>
        {value}
      </Text>
    </View>
  );
}

const LEVEL_LABELS: Record<string, string> = {
  pre_acc: 'Pre-ACC',
  acc: 'ACC',
  pcc: 'PCC',
  mcc: 'MCC',
};

function LevelChip({ label, muted, t }: { label: string; muted?: boolean; t: Translate }) {
  const display = label === 'exploring'
    ? t('intake.goalLevels.exploring.title')
    : LEVEL_LABELS[label] || label.toUpperCase();
  return (
    <View
      style={{
        borderRadius: ONBOARDING_RADII.pill,
        paddingHorizontal: ONBOARDING_SPACING.md,
        paddingVertical: ONBOARDING_SPACING.xs,
        backgroundColor: muted ? COLORS.surface : COLORS.primarySubtle,
        borderWidth: 1,
        borderColor: muted ? COLORS.border : COLORS.primary,
      }}
    >
      <Text
        style={{
          ...ONBOARDING_TYPOGRAPHY.caption,
          color: muted ? COLORS.text.muted : COLORS.primary,
          fontWeight: '600',
        }}
      >
        {display}
      </Text>
    </View>
  );
}
